import React, {
  useContext,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { matchPath, useLocation } from 'react-router-dom';
import {
  LogOut04,
  ShoppingBag01,
  Bell01,
  CalendarCheck01,
  Globe01,
  Home01,
  MarkerPin01,
  Users01,
  Container,
} from '@untitledui-pro/icons/duotone';
import { T, useT } from '@transifex/react';
import { CompanyContext } from 'src/context/company-context';
import { PropertyContext } from 'src/context/property-context';
import { useUser } from 'src/app/User.store';
import { useCompany } from 'src/app/Company.store';
import { useRoleChecker, useRouting } from 'src/routing/child-router-factory';

import { createNavData } from 'src/components/layout/sidebar/navData';
import {
  LanguagePickerModal,
  useHasMultipleLanguages,
} from 'src/components/login/LanguagePicker';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';

type NavIcon = React.ComponentType<
  React.SVGProps<SVGSVGElement> & { size?: number }
>;

const NAV_ICONS: Record<string, NavIcon> = {
  'self-service': Home01,
  containers: Container,
  collections: CalendarCheck01,
  products: ShoppingBag01,
  notifications: Bell01,
};

const FALLBACK_ICON: NavIcon = Home01;

// Gate the entrance animation to "once per page load". `performance.timeOrigin`
// is constant for the document's lifetime and changes on every fresh load —
// stamping it into sessionStorage and comparing on next mount tells us whether
// we've already animated *in this lifecycle*. Survives module re-evaluation
// (HMR), React StrictMode, and any in-app remount; resets only on a real page
// reload, which is exactly the desired UX.
//
// The animation itself is triggered imperatively via Web Animations API in a
// layout effect — not via a CSS class. CSS class-based animations replay
// whenever the class lands on a fresh DOM element, which is what was happening
// on tab focus (some upstream rerender was re-creating the elements). The
// imperative approach plays exactly when we tell it to, regardless of how
// React shuffles the DOM.
const SIDEBAR_ANIMATED_ORIGIN_KEY = 'wh-sidebar-animated-origin';
const STAGGER_ATTR = 'data-wh-sidebar-stagger';

function shouldAnimateSidebarEntrance(): boolean {
  try {
    return (
      sessionStorage.getItem(SIDEBAR_ANIMATED_ORIGIN_KEY) !==
      String(performance.timeOrigin)
    );
  } catch {
    return true;
  }
}

function markSidebarEntranceAnimated(): void {
  try {
    sessionStorage.setItem(
      SIDEBAR_ANIMATED_ORIGIN_KEY,
      String(performance.timeOrigin)
    );
  } catch {
    /* sessionStorage unavailable (private mode, disabled) — no-op */
  }
}

export default function PortalSidebar() {
  const { logo } = useContext(CompanyContext);
  const showLogo = !window.WH_SS_NO_LOGO && logo;

  const [checkRole, { routingMap }] = useRoleChecker();
  const history = useRouting.useHistory();
  const { pathname } = useLocation();
  const tx = useT();
  const { data: user, logout } = useUser();
  const property = useContext(PropertyContext);
  const { data: companyData } = useCompany();
  const productsEnabled = !!companyData?.crm_enable_products;
  const notificationsEnabled =
    (companyData?.crm_enable_container_notification_providers?.length ?? 0) > 0;

  const displayName = user?.anonymous ? null : user?.name;
  const addressLine = property?.location?.name;

  const navData = useMemo(
    () => createNavData(routingMap, { productsEnabled, notificationsEnabled }),
    [routingMap, productsEnabled, notificationsEnabled]
  );

  const items = useMemo(
    () =>
      navData
        .filter((item: $TSFixMe) => !item.roles || checkRole(item.roles))
        .map((item: $TSFixMe) => ({
          key: item.key as string,
          title: item.title as React.ReactNode,
          link: item.link as string,
          Icon: NAV_ICONS[item.key as string] ?? FALLBACK_ICON,
        })),
    [navData, checkRole]
  );

  const activeKey = useMemo(() => {
    let bestKey = '';
    let bestLen = 0;
    items.forEach(({ key, link }) => {
      if (!link) return;
      const m = matchPath({ path: link, end: false }, pathname);
      if (m && link.length > bestLen) {
        bestKey = key;
        bestLen = link.length;
      }
    });
    return bestKey;
  }, [items, pathname]);

  const [animateEntrance] = useState(shouldAnimateSidebarEntrance);
  const [isLangModalOpen, setIsLangModalOpen] = useState(false);
  const hasMultipleLanguages = useHasMultipleLanguages();
  const asideRef = useRef<HTMLElement>(null);
  useLayoutEffect(() => {
    if (!animateEntrance) return;
    markSidebarEntranceAnimated();
    if (
      typeof window !== 'undefined' &&
      window.matchMedia?.('(prefers-reduced-motion: reduce)').matches
    ) {
      return;
    }
    const root = asideRef.current;
    if (!root) return;
    const staggerEls = root.querySelectorAll<HTMLElement>(`[${STAGGER_ATTR}]`);
    if (!staggerEls.length) return;
    staggerEls.forEach((el, i) => {
      el.animate(
        [
          { opacity: 0, transform: 'translateX(-12px)' },
          { opacity: 1, transform: 'translateX(0)' },
        ],
        { duration: 300, easing: 'ease-out', delay: i * 60, fill: 'both' }
      );
    });
  }, [animateEntrance]);

  // Document-order querySelectorAll picks up these `[data-wh-sidebar-stagger]`
  // elements top-to-bottom and the layout effect animates them with index-based
  // delay. Conditional sections (logo / user / address) drop out cleanly, no
  // index bookkeeping required.
  return (
    <aside
      ref={asideRef}
      className="wh-ui flex h-full w-full flex-col justify-between bg-[var(--bg-default)] p-4"
    >
      <div className="flex flex-col gap-4">
        {showLogo && (
          <div data-wh-sidebar-stagger className="flex items-start p-4">
            <img
              src={logo}
              alt=""
              className="h-auto max-h-16 w-auto max-w-full object-contain"
            />
          </div>
        )}
        <div className="flex flex-col gap-4">
          {displayName && (
            <div
              data-wh-sidebar-stagger
              className="flex items-center gap-4 px-4 text-[16px] leading-[1.5] text-[var(--text-secondary)]"
            >
              <Users01
                size={24}
                aria-hidden
                className="shrink-0 text-[var(--icon-default)]"
              />
              <span>{displayName}</span>
            </div>
          )}
          {addressLine && (
            <div
              data-wh-sidebar-stagger
              className="flex items-center gap-4 px-4 text-[16px] leading-[1.5] text-[var(--text-secondary)]"
            >
              <MarkerPin01
                size={24}
                aria-hidden
                className="shrink-0 text-[var(--icon-default)]"
              />
              <span className="whitespace-pre-line">{addressLine}</span>
            </div>
          )}
        </div>
      </div>

      <nav
        aria-label={tx('Primary')}
        className="flex flex-1 flex-col justify-center gap-2"
      >
        {items.map(({ key, title, link, Icon }) => {
          const isActive = key === activeKey;
          return (
            <button
              key={key}
              type="button"
              data-wh-sidebar-stagger
              aria-current={isActive ? 'page' : undefined}
              onClick={() => link && history.push(link)}
              className={cn(
                'flex h-12 w-full items-center gap-4 rounded-[8px] px-4 py-2 text-left text-[16px] font-medium leading-[1.5] focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--border-focus)]',
                isActive
                  ? 'bg-[var(--bg-active)] text-[var(--text-primary)]'
                  : 'text-[var(--text-secondary)] hover:bg-[var(--bg-hover)]'
              )}
            >
              <Icon
                size={24}
                aria-hidden
                className={cn(
                  'shrink-0',
                  isActive
                    ? 'text-[var(--icon-active)]'
                    : 'text-[var(--icon-default)]'
                )}
              />
              <span>{title}</span>
            </button>
          );
        })}
      </nav>

      <div className="flex flex-col gap-2">
        <div data-wh-sidebar-stagger>
          {hasMultipleLanguages && (
            <button
              type="button"
              onClick={() => setIsLangModalOpen(true)}
              className="flex h-12 w-full items-center justify-center gap-4 rounded-[8px] border border-[var(--border-default)] bg-[var(--bg-default)] px-4 text-[16px] font-medium leading-[1.5] text-[var(--text-secondary)] shadow-[0_1px_0_var(--border-default)] transition-colors hover:bg-[var(--bg-hover)] focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--border-focus)]"
            >
              <Globe01 className="size-6" aria-hidden />
              <T _str="Change language" />
            </button>
          )}
        </div>
        <Button
          variant="outline"
          onClick={logout}
          data-wh-sidebar-stagger
          className="h-12 w-full justify-center gap-4 rounded-[8px] border-[var(--border-default)] bg-[var(--bg-surface)] px-4 text-[16px] font-medium leading-[1.5] text-[var(--text-secondary)] shadow-[0_1px_0_var(--border-default)] hover:bg-[var(--bg-hover)] hover:text-[var(--text-secondary)]"
        >
          <LogOut04 className="size-6" aria-hidden />
          <T _str="Log out" />
        </Button>
      </div>
      {hasMultipleLanguages && (
        <LanguagePickerModal
          open={isLangModalOpen}
          onOpenChange={setIsLangModalOpen}
        />
      )}
    </aside>
  );
}
